/**
 * LiteAPI — Shared API service for Lite CMS views.
 *
 * Wraps the existing full CMS DataHelper patterns but adapts them
 * for Lite's functional component architecture. Uses the same
 * backend API actions (get_files, create_file, get_playlists, etc.)
 * so no new backend endpoints are needed.
 *
 * Usage:
 *   import LiteAPI from '../liteAPI';
 *
 *   // In a component that receives Redux props:
 *   const files = await LiteAPI.getFiles(brandId, props);
 *   await LiteAPI.uploadFile(file, brandId, props, onProgress);
 *   await LiteAPI.deleteFile(fileId, brandId, props);
 */

import axios from "axios";
import DataHelper from "../../../helpers/DataHelper";
import liteApp from "../Firebase_lite";
import { SERVER_CONFIG } from "../../../Config";
import firebase from "../Firebase_lite";

// Guard: checks that the Lite Firebase named app has a working Realtime Database.
// Returns false (silently) if databaseURL is missing or the app isn't ready.
const isFirebaseReady = () => {
  try {
    liteApp.database(); // throws if databaseURL is missing or DB not initialized
    return true;
  } catch (_) {
    return false;
  }
};

class LiteAPI {
  // ─── Media / Files ───────────────────────────────────────────

  /**
   * Fetch all files for a brand.
   * Returns normalized array: [{ id, name, type, size, duration, url, previewUrl, uploadedAt, raw }]
   */
  static getFiles = (brandId, props) => {
    const params = { brandid: brandId };
    return DataHelper.getFiles(params, props).then((files) => {
      return files.map((f) => LiteAPI.normalizeFileData(f));
    });
  };

  /**
   * Upload a single file. Returns a promise that resolves when upload is complete.
   * @param {File} file - The file object from input/drop
   * @param {string} brandId - Brand ID
   * @param {object} props - Redux props (must include requestFilesServerPost, usertoken)
   * @param {function} onProgress - Callback with percentage (0-100)
   */
  static uploadFile = (file, brandId, props, onProgress = () => {}) => {
    const { requestFilesServerPost, usertoken } = props;

    return new Promise((resolve, reject) => {
      const params = new FormData();
      params.append("file[]", file);
      params.append(
        "filetype",
        file.type?.startsWith("video") ? "video" : "image",
      );
      params.append("brandid", brandId);
      params.append("fileproperties", JSON.stringify({}));

      const config = {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(Math.min(percent, 99));
        },
      };

      requestFilesServerPost(
        "create_file",
        params,
        { "Content-Type": "multipart/form-data" },
        (response) => {
          onProgress(100);
          resolve(response.data);
        },
        usertoken,
        config,
      );
    });
  };

  /**
   * Delete a file permanently: move to trash then delete from trash.
   */
  static deleteFile = (fileId, brandId, props) => {
    const { requestServerPost, usertoken } = props;
    const params = { fileids: `${fileId}`, brandid: brandId };

    return new Promise((resolve, reject) => {
      requestServerPost(
        "update_trashs",
        params,
        {},
        () => {
          requestServerPost(
            "delete_trashs",
            params,
            {},
            (response) => {
              resolve(response);
            },
            usertoken,
          );
        },
        usertoken,
      );
    });
  };

  /**
   * Rename a file. Reuses the same "update_file" action the Pro CMS
   * uses for renaming (see MediaListElement.js updateMediaName).
   */
  static renameFile = (fileId, newName, props) => {
    const { requestServerPost, usertoken } = props;
    const params = { fileid: fileId, filename: newName };

    return new Promise((resolve, reject) => {
      requestServerPost(
        "update_file",
        params,
        {},
        (response) => {
          resolve(response.data);
        },
        usertoken,
      );
    });
  };

  /**
   * Check if files are in use before deleting.
   * Returns array of usage info (empty = safe to delete).
   */
  static checkFileUsage = (fileId, brandId, props) => {
    const { requestServerPost, usertoken } = props;
    const params = { fileids: `${fileId}`, brandid: brandId };

    return new Promise((resolve) => {
      requestServerPost(
        "check_files_usage",
        params,
        {},
        (response) => {
          resolve(response.data?.data || []);
        },
        usertoken,
      );
    });
  };

  // ─── Playlists ───────────────────────────────────────────────

  /**
   * Fetch all playlists for a brand.
   * Returns normalized array of playlists with their media items.
   */
  static getPlaylists = (brandId, props) => {
    const params = { brandid: brandId };
    return DataHelper.getPlaylists(params, props).then((playlists) => {
      return playlists.map((p) => LiteAPI.normalizePlaylistData(p));
    });
  };

  /**
   * Add a media item to a playlist.
   */
  static addMediaToPlaylist = (
    playlistId,
    fileId,
    brandId,
    props,
    duration = 10,
  ) => {
    const { requestServerPost, usertoken } = props;
    const params = {
      playlistid: playlistId,
      brandid: brandId,
      medianame: "",
      startat: 0,
      duration: duration,
      fileid: fileId,
      scale: "fill",
      startdate: "",
      enddate: "",
    };

    return new Promise((resolve) => {
      requestServerPost(
        "create_media",
        params,
        {},
        (response) => {
          resolve(response.data);
        },
        usertoken,
      );
    });
  };

  /**
   * Remove a media item from a playlist.
   */
  static deleteMedia = (mediaId, props) => {
    const { requestServerPost, usertoken } = props;
    const params = { mediaid: mediaId };

    return new Promise((resolve) => {
      requestServerPost(
        "delete_media",
        params,
        {},
        (response) => {
          resolve(response.data);
        },
        usertoken,
      );
    });
  };

  /**
   * Update media order/duration in a playlist.
   */
  static updateMedia = (mediaId, updates, props) => {
    const { requestServerPost, usertoken } = props;
    const params = { mediaid: mediaId, ...updates };

    return new Promise((resolve) => {
      requestServerPost(
        "update_media",
        params,
        {},
        (response) => {
          resolve(response.data);
        },
        usertoken,
      );
    });
  };

  // ─── Screens ─────────────────────────────────────────────────

  /**
   * Fetch screens for a brand.
   * Uses the v1 API endpoint directly with Bearer token auth.
   */
  static getScreens = async (brandId, branchId, props) => {
    const { usertoken } = props;

    try {
      const response = await axios({
        method: "get",
        url: "https://cds.zignstack.com/api_new/v1/get_screens",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${usertoken}`,
        },
        data: { brandid: brandId },
      });

      const screens = response.data?.data ?? response.data ?? [];
      if (!Array.isArray(screens)) return [];

      return screens.map((s) => LiteAPI.normalizeScreenData(s));
    } catch (err) {
      console.error("getScreens error:", err);
      return [];
    }
  };

  /**
   * Pair a screen using a 6-digit code.
   * Follows the same 3-step flow as the main CMS:
   *   1. checkscreenpairing  — verify screen is online (Node server)
   *   2. create_screen        — register screen in CMS
   *   3. attemptscreenpairing — signal the device (Node server)
   */
  static pairScreen = (pairingCode, brandId, branchId, props) => {
    const { requestServerPost, requestNodeServerPost, usertoken } = props;
    const nodeHeaders = { "Content-Type": "application/x-www-form-urlencoded" };

    return new Promise((resolve, reject) => {
      // ── Step 1: Check screen is available and online ──
      const checkParams = new URLSearchParams();
      checkParams.append("code", pairingCode);

      requestNodeServerPost(
        "checkscreenpairing",
        checkParams,
        nodeHeaders,
        (checkRes) => {
          if (!checkRes.data?.result || checkRes.data.result !== 1) {
            reject(
              new Error(
                "No screen found with this code. Make sure your screen is connected to the internet.",
              ),
            );
            return;
          }

          // ── Step 2: Create screen record in CMS ──
          const createParams = new FormData();
          createParams.append("brandid", brandId);
          createParams.append("branchid", branchId);
          createParams.append("screenname", "LiteScreen"); // renamed after ScreenCode is known
          createParams.append("pairingcode", pairingCode);

          requestServerPost(
            "create_screen_lite",
            createParams,
            {},
            (createRes) => {
              const screenSettings = createRes.data;
              if (screenSettings?.status === false && screenSettings?.message) {
                reject(new Error(screenSettings.message));
                return;
              }
              if (!screenSettings?.AccountCode) {
                reject(
                  new Error("Failed to register screen. Please try again."),
                );
                return;
              }

              // Clear any pending unpair request before pairing
              LiteAPI.clearUnpairRequest(screenSettings);

              // ── Step 3: Signal the device to pair (500ms delay matches main CMS) ──
              setTimeout(() => {
                const pairParams = new URLSearchParams();
                pairParams.append("account", screenSettings.AccountCode);
                pairParams.append("brand", screenSettings.BrandCode);
                pairParams.append("branch", screenSettings.BranchCode);
                pairParams.append("screen", screenSettings.ScreenCode);
                pairParams.append(
                  "screenname",
                  `MyScreen-${screenSettings.ScreenCode}`,
                );
                pairParams.append("code", pairingCode);

                requestNodeServerPost(
                  "attemptscreenpairing",
                  pairParams,
                  nodeHeaders,
                  (pairRes) => {
                    if (pairRes.data?.status === "success") {
                      resolve(screenSettings);
                    } else {
                      reject(
                        new Error(
                          "Screen pairing failed. Please check the code and try again.",
                        ),
                      );
                    }
                  },
                  usertoken,
                );
              }, 500);
            },
            usertoken,
          );
        },
        usertoken,
      );
    });
  };

  /**
   * Update screen name.
   */
  static updateScreenName = (
    screenId,
    screenName,
    brandId,
    branchId,
    props,
  ) => {
    const { requestServerPost, usertoken } = props;
    const params = new FormData();
    params.append("brandid", brandId);
    params.append("branchid", branchId);
    params.append("screenname", screenName);
    params.append("screenid", screenId);

    return new Promise((resolve) => {
      requestServerPost(
        "update_screen",
        params,
        {},
        (response) => resolve(response.data),
        usertoken,
      );
    });
  };

  /**
   * Update screen resolution (width & height).
   */
  static updateScreenResolution = (
    screenId,
    width,
    height,
    brandId,
    branchId,
    props,
    campaignId,
  ) => {
    const { requestServerPost, usertoken } = props;
    const params = new FormData();
    params.append("brandid", brandId);
    params.append("branchid", branchId);
    params.append("screenid", screenId);
    params.append("screenwidth", width);
    params.append("screenheight", height);
    if (campaignId) params.append("campaignid", campaignId);

    return new Promise((resolve) => {
      requestServerPost(
        "update_screen",
        params,
        {},
        (response) => resolve(response.data),
        usertoken,
      );
    });
  };

  /**
   * Persist timezone to the device via Firebase.
   */
  static updateScreenTimezone = (rawScreen, timezone) => {
    if (!isFirebaseReady()) return Promise.resolve();
    const {
      AccountCode = "",
      BrandCode = "",
      BranchCode = "",
      ScreenCode = "",
    } = rawScreen;
    if (!AccountCode || !ScreenCode) return Promise.resolve();
    try {
      const db = liteApp
        .database()
        .ref()
        .child(`${AccountCode}/${BrandCode}/${BranchCode}/${ScreenCode}`);
      return db.child("timezone").set(timezone);
    } catch (_) {
      return Promise.resolve();
    }
  };

  /**
   * Publish content to screens.
   * Calls publish_content directly instead of DataHelper.publishContent so we can
   * guard the Firebase push (Firebase may not be initialized on all servers).
   */
  static publishContent = (screenDatas, props) => {
    const { requestServerPost, usertoken } = props;
    const screenIDs = screenDatas.map((s) => s.ScreenID).join(",");

    return new Promise((resolve) => {
      requestServerPost(
        "publish_content",
        { screenids: screenIDs },
        {},
        () => {
          // Signal devices via Node API
          screenDatas.forEach((sData) => {
            const {
              AccountCode = "",
              BrandCode = "",
              BranchCode = "",
              ScreenCode = "",
            } = sData;

            if (!AccountCode || !ScreenCode) return;

            // if (isFirebaseReady() && AccountCode && ScreenCode) {
            //   const db = liteApp
            //     .database()
            //     .ref()
            //     .child(
            //       `${AccountCode}/${BrandCode}/${BranchCode}/${ScreenCode}`,
            //     );

            //   if (db) {
            //     db.child("needs_download_content").set(0);
            //     db.child("needs_download_content").set(1);
            //   }
            // }

            fetch(
              `https://cds.zignstack.com/nodeapi/api/needsDownloadContent/${AccountCode}/${BrandCode}/${BranchCode}/${ScreenCode}`,
              { method: "POST" },
            ).catch(() => {});
          });
          resolve();
        },
        usertoken,
      );
    });
  };

  /**
   * Unpair a screen: signal the device via Firebase then delete from CMS.
   */
  static unpairScreen = async (rawScreen, props) => {
    const { requestServerPost, usertoken } = props;
    const {
      AccountCode = "",
      BrandCode = "",
      BranchCode = "",
      ScreenCode = "",
      ScreenID,
    } = rawScreen;

    // Signal device to unpair via Firebase
    if (isFirebaseReady() && AccountCode && ScreenCode) {
      try {
        const db = liteApp
          .database()
          .ref()
          .child(`${AccountCode}/${BrandCode}/${BranchCode}/${ScreenCode}`);
        await db.child("needs_unpair").set(1);
        await db.child("is_paired").set(0);
        await db.child("os").set("");
        await db.child("os_version").set("");
      } catch (_) {}
    }

    // Delete screen record from CMS
    if (ScreenID) {
      await new Promise((resolve) => {
        requestServerPost(
          "delete_screen",
          { screenid: ScreenID },
          {},
          () => resolve(),
          usertoken,
        );
      });
    }
  };

  /**
   * Clear Firebase unpair flag for a screen (prevents device self-unpairing after pair).
   */
  static clearUnpairRequest = (rawScreen) => {
    if (!isFirebaseReady()) return;
    try {
      DataHelper.clearScreenUnpariRequest(rawScreen);
    } catch (_) {}
  };

  /**
   * Subscribe to real-time device status from Firebase for a screen.
   * Calls onChange(data) immediately and on every change.
   * Returns an unsubscribe function — call it in useEffect cleanup.
   *
   * Relevant Firebase keys: os, os_version, player_version, app_version, device_model
   */
  static subscribeScreenStatus = (rawScreen, onChange) => {
    if (!isFirebaseReady()) return () => {};
    const {
      AccountCode = "",
      BrandCode = "",
      BranchCode = "",
      ScreenCode = "",
    } = rawScreen || {};
    if (!AccountCode || !ScreenCode) return () => {};
    try {
      const db = liteApp
        .database()
        .ref()
        .child(`${AccountCode}/${BrandCode}/${BranchCode}/${ScreenCode}`);
      const handler = (snap) => {
        if (snap.exists()) onChange(snap.val());
      };
      db.on("value", handler);
      return () => db.off("value", handler);
    } catch (_) {
      return () => {};
    }
  };

  // ─── Campaigns & Timelines ───────────────────────────────────

  /**
   * Fetch campaigns for a brand.
   */
  static getCampaigns = (brandId, props) => {
    const { requestServerGet, usertoken } = props;
    return new Promise((resolve) => {
      requestServerGet(
        "get_campaigns",
        { brandid: brandId },
        null,
        (response) => {
          resolve(response.data || []);
        },
        usertoken,
      );
    });
  };

  /**
   * Get timelines for a campaign.
   */
  static getTimelines = (brandId, campaignId, props) => {
    const { requestServerGet, usertoken } = props;
    return new Promise((resolve) => {
      requestServerGet(
        "get_timelines",
        { brandid: brandId, campaignid: campaignId },
        null,
        (response) => {
          resolve(response.data || []);
        },
        usertoken,
      );
    });
  };

  /**
   * Create a campaign.
   */
  static createCampaign = (name, brandId, props) => {
    const { requestServerPost, usertoken } = props;
    const params = new FormData();
    params.append("brandid", brandId);
    params.append("campaignname", name);
    params.append("campaigncolor", "#6366f1"); // Lite theme color

    return new Promise((resolve) => {
      requestServerPost(
        "create_campaign",
        params,
        { "Content-Type": "multipart/form-data" },
        (response) => {
          resolve(response.data);
        },
        usertoken,
      );
    });
  };

  /**
   * Update timeline layout channels (assigns a playlist to a slot).
   */
  static updateTimelineLayout = (
    timelineLayoutId,
    brandId,
    channels,
    props,
  ) => {
    const { requestServerPost, usertoken } = props;
    const params = new FormData();
    params.append("brandid", brandId);
    params.append("timelinelayoutid", timelineLayoutId);
    params.append("channels", JSON.stringify(channels));
    params.append("duration", "86400"); // 24h default
    params.append("startplaytime", "");

    return new Promise((resolve) => {
      requestServerPost(
        "update_timelinelayout",
        params,
        { "Content-Type": "multipart/form-data" },
        (response) => {
          resolve(response.data);
        },
        usertoken,
      );
    });
  };

  /**
   * Smart playlist resolution for a screen.
   *
   * Logic:
   * 1. If screen has a CampaignID → get its timelines
   * 2. For the first timeline, parse its layout
   * 3. If layout has exactly 1 slot (fullscreen) → extract the linked playlist ID
   * 4. Return that playlist ID
   * 5. If no valid campaign/layout found → return null (caller should create one)
   *
   * @returns {{ playlistId, campaignId, timelineLayoutId, isFullscreen }} or null
   */
  static resolvePlaylistForScreen = async (screen, brandId, props) => {
    const rawScreen = screen.raw || screen;
    const campaignId = rawScreen.CampaignID;

    if (!campaignId || campaignId === "") {
      return null; // No campaign linked to this screen
    }

    try {
      // Get timelines for this campaign
      const timelines = await LiteAPI.getTimelines(brandId, campaignId, props);
      if (!timelines || timelines.length === 0) return null;

      const timeline = timelines[0];
      const timelineLayouts = timeline.Timelinelayouts;

      if (!timelineLayouts || timelineLayouts.length === 0) return null;

      const tll = timelineLayouts[0];
      const layoutJSON =
        typeof tll.Layout === "string" ? JSON.parse(tll.Layout) : tll.Layout;
      const channelJSON =
        typeof tll.Channels === "string"
          ? JSON.parse(tll.Channels)
          : tll.Channels;

      const slots = layoutJSON?.slots || [];
      const isFullscreen = slots.length === 1;

      if (isFullscreen && slots.length > 0) {
        const slotId = slots[0].slot_id;
        const linkedPlaylistId = channelJSON?.[slotId];

        if (linkedPlaylistId && linkedPlaylistId !== -1) {
          return {
            playlistId: linkedPlaylistId,
            campaignId: campaignId,
            timelineLayoutId: tll.TLLID,
            isFullscreen: true,
          };
        }
      }

      // Campaign exists but layout is multi-zone or no playlist linked
      return {
        playlistId: null,
        campaignId: campaignId,
        timelineLayoutId: tll.TLLID,
        isFullscreen,
      };
    } catch (err) {
      console.error("resolvePlaylistForScreen error:", err);
      return null;
    }
  };

  // ─── Brands & Branches ───────────────────────────────────────

  /**
   * Fetch branches for a brand.
   */
  static getBranches = (brandId, props) => {
    const { requestServerGet, usertoken } = props;
    const params = { brandid: brandId };

    return new Promise((resolve) => {
      requestServerGet(
        "get_branches",
        params,
        null,
        (response) => {
          resolve(response.data || []);
        },
        usertoken,
      );
    });
  };

  /**
   * Get brands for the current user.
   */
  static getBrands = (props) => {
    const { requestServerGet, usertoken } = props;

    return new Promise((resolve) => {
      requestServerGet(
        "get_brands",
        {},
        null,
        (response) => {
          resolve(response.data || []);
        },
        usertoken,
      );
    });
  };

  // ─── Data Normalizers ────────────────────────────────────────
  // These convert full CMS data structures to the simpler format
  // that Lite views expect. This decouples Lite from backend schema.

  static normalizeFileData = (rawFile) => {
    const serverUri = SERVER_CONFIG.SERVER_URI || "";
    const isVideo = rawFile.type === "video";
    const uploadFile = rawFile.UploadFile || "";
    const fileUrl = uploadFile.startsWith("http")
      ? uploadFile
      : `${serverUri}uploads/${uploadFile}`;

    // Thumbnail: use ThumbFile if available, otherwise use the file itself for images
    const thumbFile = rawFile.ThumbFile || "";
    const thumbUrl = thumbFile
      ? thumbFile.startsWith("http")
        ? thumbFile
        : `${serverUri}uploads/${thumbFile}`
      : isVideo
        ? null
        : fileUrl;

    return {
      id: rawFile.FileID,
      name: rawFile.FileName || uploadFile,
      type: rawFile.type || "image",
      size: rawFile.FileSize ? LiteAPI.formatFileSize(rawFile.FileSize) : "—",
      sizeBytes: rawFile.FileSize || 0,
      duration: rawFile.Duration ? `${rawFile.Duration}s` : "10s",
      durationSeconds: rawFile.Duration || 10,
      url: fileUrl,
      previewUrl: thumbUrl,
      uploadedAt: rawFile.CreateDate || rawFile.created_at || "—",
      // Keep raw data for when we need to pass to full CMS APIs
      raw: rawFile,
    };
  };

  static normalizePlaylistData = (rawPlaylist) => {
    const serverUri = SERVER_CONFIG.SERVER_URI || "";
    const toAbsUrl = (path) => {
      if (!path) return "";
      return path.startsWith("http") ? path : `${serverUri}uploads/${path}`;
    };

    // The server has been observed to return the same MediaID twice in one
    // response (e.g. a join fan-out) right after a media row is created —
    // collapse those before mapping so the UI never shows a media item twice.
    const seenMediaIds = new Set();
    const dedupedMedias = (rawPlaylist.Medias || []).filter((m) => {
      if (seenMediaIds.has(m.MediaID)) return false;
      seenMediaIds.add(m.MediaID);
      return true;
    });

    const medias = dedupedMedias.map((m) => ({
      id: m.MediaID,
      name: m.MediaName || m.FileName || "",
      type: m.type || "image",
      duration: m.Duration ? `${m.Duration}s` : "10s",
      durationSeconds: m.Duration || 10,
      fileId: m.FileID,
      url: toAbsUrl(m.UploadFile),
      previewUrl: toAbsUrl(m.ThumbFile || m.UploadFile),
      raw: m,
    }));

    return {
      id: rawPlaylist.PlaylistID,
      name: rawPlaylist.PlaylistName || "Untitled",
      items: medias,
      createdAt: rawPlaylist.CreateDate || "",
      raw: rawPlaylist,
    };
  };

  static normalizeScreenData = (rawScreen) => {
    return {
      id: rawScreen.ScreenID,
      name: rawScreen.ScreenName || "Screen",
      code: rawScreen.ScreenCode || "",
      isOnline: rawScreen.IsOnline || false,
      lastSeen: rawScreen.LastSeen || null,
      playlistId: rawScreen.PlaylistID || null,
      OS: rawScreen.OS || "",
      OSVersion: rawScreen.OSVersion || "",
      AppVersion: rawScreen.AppVersion || "",
      PlayerVersion: rawScreen.PlayerVersion || "",
      DeviceModel: rawScreen.DeviceModel || "",
      ScreenWidth: rawScreen.ScreenWidth || null,
      ScreenHeight: rawScreen.ScreenHeight || null,
      raw: rawScreen,
    };
  };

  // ─── Utilities ───────────────────────────────────────────────

  static formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
  };

  /**
   * Calculate total storage used.
   * @param {Array} files - Array of normalized file objects
   * @returns {{ used: number, usedFormatted: string, limitMB: number, percentage: number }}
   */
  static getStorageUsage = (files, limitMB = 500) => {
    const totalBytes = files.reduce((sum, f) => sum + (f.sizeBytes || 0), 0);
    const limitBytes = limitMB * 1024 * 1024;
    return {
      used: totalBytes,
      usedFormatted: LiteAPI.formatFileSize(totalBytes),
      limitMB,
      limitFormatted: `${limitMB} MB`,
      percentage: Math.round((totalBytes / limitBytes) * 100),
    };
  };

  // ─── Account Storage ─────────────────────────────────────────

  /**
   * Fetch account storage usage from the server.
   * Returns the raw data object from the API response.
   */
  static getAccountStorage = async (brandId, userToken) => {
    // console.log("userToken", userToken);
    const url = `${SERVER_CONFIG.SERVER_URI}${SERVER_CONFIG.API_PATH}get_accountstoragenew`;
    const response = await axios.get(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    });
    return response.data?.data ?? response.data ?? null;
  };
}

export default LiteAPI;
